import React, { useMemo, useState } from "react";
import * as XLSX from "xlsx";
import "../../styles/App.css";
import {
  findHeaderRow,
  classifyRow,
  mergeReport,
  findDiscrepancies,
  computeCutoff,
  buildSummary,
  sortReportRows,
  applyReportFilters,
  anonymizeRows,
  formatScore,
  splitNoSession,
  reportExportRow,
  discrepancyExportRow,
  applyDefaultZone,
  countRowsWithIssue,
  countImportIssues,
  collectClampEvents,
  buildAnalyzePayload,
  applyCommittedBatch,
  REQUIRED_COLUMNS,
} from "../../decision/importColumns.mjs";
import { analyzeAllocation, DssApiError } from "../../api/dss.js";
import { exportMultiSheetToExcel } from "../../components/ExportToExcel.js";
import ColumnMapper from "./ColumnMapper.js";
import PrivilegeSelect from "./PrivilegeSelect.js";
import ImportReportTable from "./ImportReportTable.js";
import ImportReportFilters from "./ImportReportFilters.js";
import ImportDiscrepanciesBlock from "./ImportDiscrepanciesBlock.js";
import CounterfactualPanel from "./CounterfactualPanel.js";
import ModelCalculationPanel from "./ModelCalculationPanel.js";
import DefaultZoneBlock from "./DefaultZoneBlock.js";
import ImportToSystemPanel from "./ImportToSystemPanel.js";
import { sha256Hex } from "../../decision/fileHash.js";

const RUN_TYPES = [
  { value: "settlement", label: "Поселення на новий навчальний рік" },
  { value: "relocation", label: "Переселення за підсумками семестру" },
];

const PREVIEW_LIMIT = 20;

const CLAMPED_BACKGROUND = "#fff3cd";

const FIELD_LABELS = {
  ses: "бал за сесію",
  dorm: "бал за гуртожиток",
  act: "бал за діяльність",
  zone: "віддаленість",
};

function issueLabel(issue) {
  const field = FIELD_LABELS[issue.field] || issue.field;
  switch (issue.type) {
    case "empty":
      return `не вказано: ${field}`;
    case "clamped":
      return `обрізано до максимуму: ${field}`;
    case "no_session":
      return "без сесії (бал нижче 30 або не вказано) — окрема група після загального конкурсу";
    case "zone_missing":
      return "віддаленість не вказана";
    case "zone_default":
      return "віддаленість взято за замовчуванням";
    case "privilege_default":
      return "пільгу не вказано, прийнято 1 (без пільги)";
    default:
      return `поза шкалою: ${field}`;
  }
}

// Комірка бала/рівня з підсвіткою обрізання: tooltip "у файлі 27, взято 25".
function ScoreCell({ row, field }) {
  const clamped = row.issues.find((i) => i.type === "clamped" && i.field === field);
  const noSession = row.issues.some((i) => i.type === "no_session" && i.field === field);
  const value = row.values[field];
  const text = value === null || value === undefined ? "—" : value;
  if (noSession) {
    return (
      <td
        className="table-content"
        style={{ fontStyle: "italic" }}
        title="Без сесії: бал нижче 30 або не вказано — здобувач іде в окрему групу після загального конкурсу"
        data-testid={`no-session-${row.id}`}
      >
        {text} (без сесії)
      </td>
    );
  }
  if (!clamped) return <td className="table-content">{text}</td>;
  return (
    <td
      className="table-content"
      style={{ backgroundColor: CLAMPED_BACKGROUND }}
      title={`у файлі ${clamped.original}, взято ${value}`}
      data-testid={`clamped-${row.id}-${field}`}
    >
      {text}
    </td>
  );
}

function excludeReasonLabel(reason) {
  if (reason === "out_of_range") return "значення поза межами шкали";
  if (reason === "no_scores") return "немає жодного бала";
  return "";
}

// Комірка "Проблеми" попереднього перегляду: виключений рядок -- причина
// (жирним, червоним) і перелік проблем у дужках; прийнятий -- перелік
// проблем або "—".
function RowIssuesCell({ row }) {
  const issuesText = row.issues.map(issueLabel).join(", ");
  if (row.excluded) {
    return (
      <strong style={{ color: "var(--full-busy)" }}>
        {excludeReasonLabel(row.excludeReason)}
        {issuesText ? ` (${issuesText})` : ""}
      </strong>
    );
  }
  return issuesText || "—";
}

// Кількість актів з клітинки файлу: ціле >= 0 або null, якщо порожньо/нечислово.
function actsCount(raw) {
  if (raw === undefined || raw === null || String(raw).trim() === "") return null;
  const n = Number(raw);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

const AllocationImportPage = () => {
  const [fileName, setFileName] = useState("");
  const [workbook, setWorkbook] = useState(null);
  const [sheetName, setSheetName] = useState("");
  const [headerInfo, setHeaderInfo] = useState(null); // { rowIndex, indices, foundHeaders, headerEntries, missing, dataRows }
  const [manualIndices, setManualIndices] = useState({});
  const [parseError, setParseError] = useState("");

  const [places, setPlaces] = useState("");
  const [placesTouched, setPlacesTouched] = useState(false);
  const [runType, setRunType] = useState("settlement");
  const [onlyLosing, setOnlyLosing] = useState(false);
  // Пільга, виправлена вручну (id -> 1..3); інакше береться з файлу або 1.
  const [privilegeById, setPrivilegeById] = useState({});
  // Віддаленість за замовчуванням для рядків із zone_missing (null -- не задано).
  const [defaultZone, setDefaultZone] = useState(null);
  // SHA-256 вибраного файлу (для "Завантажити в систему"); null -- хеш не
  // вдалося порахувати.
  const [fileHash, setFileHash] = useState(null);

  const [generating, setGenerating] = useState(false);
  const [analyzeError, setAnalyzeError] = useState("");
  const [analyzeResult, setAnalyzeResult] = useState(null);
  const [sortKey, setSortKey] = useState("modelRank");
  const [sortDir, setSortDir] = useState("asc");
  const [filters, setFilters] = useState({});
  // Режим нормалізації: абсолютний -- основний. Стан "Знеособити" навмисно
  // не зберігається між сесіями (ні localStorage, ні URL) і типово вимкнений.
  const [normalization, setNormalization] = useState("absolute");
  const [anonymize, setAnonymize] = useState(false);
  const [selectedRowId, setSelectedRowId] = useState(null);

  const effectiveIndices = useMemo(() => {
    if (!headerInfo) return null;
    return { ...headerInfo.indices, ...manualIndices };
  }, [headerInfo, manualIndices]);

  const missingColumns = useMemo(() => {
    if (!effectiveIndices) return [];
    // zone і privilege необов'язкові: їхня відсутність не блокує імпорт.
    return REQUIRED_COLUMNS.filter(
      (key) => effectiveIndices[key] === null || effectiveIndices[key] === undefined
    );
  }, [effectiveIndices]);

  // Розбір усіх рядків даних (після рядка заголовків) на "заявників" +
  // проблеми валідації -- обчислюється лише коли усі колонки зіставлено.
  const cellAt = (row, key) => {
    const index = effectiveIndices[key];
    return index === null || index === undefined ? undefined : row[index];
  };

  const baseRows = useMemo(() => {
    if (!headerInfo || missingColumns.length > 0) return [];
    return headerInfo.dataRows.map((row, i) => {
      const pib = row[effectiveIndices.pib];
      const room = row[effectiveIndices.room];
      const classification = classifyRow({
        ses: row[effectiveIndices.ses],
        dorm: row[effectiveIndices.dorm],
        act: row[effectiveIndices.act],
        zone: cellAt(row, "zone"),
        privilege: cellAt(row, "privilege"),
      });
      return {
        rowNumber: i + 1,
        id: i + 1,
        pib: pib === undefined || pib === null ? "" : String(pib).trim(),
        room: room === undefined || room === null ? "" : String(room).trim(),
        // Сирі значення комірок: у систему йдуть вони, а не обрізані, щоб
        // сервер сам фіксував clamped/empty.
        raw: {
          ses: row[effectiveIndices.ses],
          dorm: row[effectiveIndices.dorm],
          act: row[effectiveIndices.act],
          zone: cellAt(row, "zone"),
          privilege: cellAt(row, "privilege"),
        },
        // Акти з файлу -- лише для показу, у розрахунок не йдуть.
        acts: actsCount(cellAt(row, "acts")),
        ...classification,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [headerInfo, effectiveIndices, missingColumns.length]);

  // "Задати віддаленість за замовчуванням" лише накладається на розібрані
  // рядки (applyDefaultZone), тож "Скасувати" -- це просто defaultZone = null.
  const parsedRows = useMemo(
    () => (defaultZone === null ? baseRows : applyDefaultZone(baseRows, defaultZone)),
    [baseRows, defaultZone]
  );

  const acceptedRows = useMemo(() => parsedRows.filter((r) => !r.excluded), [parsedRows]);
  const excludedCount = parsedRows.length - acceptedRows.length;
  const zoneMissingCount = countRowsWithIssue(acceptedRows, "zone_missing");
  const zoneDefaultCount = countRowsWithIssue(acceptedRows, "zone_default");

  const effectivePlaces = placesTouched
    ? Number(places) || 0
    : acceptedRows.length;

  const hasActsColumn = effectiveIndices?.acts !== null && effectiveIndices?.acts !== undefined;

  const getPrivilege = (row) =>
    privilegeById[row.id] === undefined ? row.values.privilege : privilegeById[row.id];

  // Рядки для POST /api/import/rating/preview: лише розпізнані значення.
  const uploadRows = useMemo(() => {
    const cell = (v) => {
      if (v === undefined || v === null) return null;
      return typeof v === "number" || typeof v === "string" ? v : String(v);
    };
    return baseRows.map((r) => ({
      rowNumber: r.rowNumber,
      pib: r.pib,
      room: r.room,
      ses: cell(r.raw.ses),
      dorm: cell(r.raw.dorm),
      act: cell(r.raw.act),
      zone: cell(r.raw.zone),
      privilege: privilegeById[r.id] === undefined ? cell(r.raw.privilege) : privilegeById[r.id],
    }));
  }, [baseRows, privilegeById]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setFileName(file.name);
    setParseError("");
    setHeaderInfo(null);
    setManualIndices({});
    setAnalyzeResult(null);
    setAnalyzeError("");
    setPlacesTouched(false);
    setPrivilegeById({});
    setDefaultZone(null);
    setFileHash(null);

    try {
      const buffer = await file.arrayBuffer();
      try {
        setFileHash(await sha256Hex(buffer));
      } catch (hashErr) {
        console.error(hashErr);
      }
      const wb = XLSX.read(buffer, { type: "array" });
      setWorkbook(wb);
      const firstSheet = wb.SheetNames[0];
      setSheetName(firstSheet);
      loadSheet(wb, firstSheet);
    } catch (err) {
      console.error(err);
      setParseError("Не вдалося розібрати файл. Перевірте, що це коректний .xlsx файл.");
    }
  };

  const loadSheet = (wb, name) => {
    const worksheet = wb.Sheets[name];
    applySheetRows(XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: "" }));
  };

  // Розбір аркуша: пошук рядка заголовків і рядків даних.
  const applySheetRows = (rows) => {
    const found = findHeaderRow(rows);
    if (!found) {
      setHeaderInfo(null);
      setParseError(
        "Не вдалося знайти рядок заголовків на цьому аркуші. Оберіть інший аркуш, якщо доступно."
      );
      return;
    }
    const dataRows = rows.slice(found.rowIndex + 1).filter((row) =>
      row.some((cell) => cell !== null && cell !== undefined && String(cell).trim() !== "")
    );
    setHeaderInfo({ ...found, dataRows });
    setManualIndices({});
    setParseError("");
  };

  const handleSheetChange = (name) => {
    setSheetName(name);
    setAnalyzeResult(null);
    setPlacesTouched(false);
    setPrivilegeById({});
    setDefaultZone(null);
    if (workbook) loadSheet(workbook, name);
  };

  const handleManualColumnChange = (key, index) => {
    setManualIndices((prev) => ({ ...prev, [key]: index }));
  };

  // Запит до DSS і звіт: спільне для «Розрахувати» і для автоматичного
  // розрахунку після фіксації імпорту. source -- лише для пояснення під звітом.
  const runAnalyze = async (applicants, mode, placesForRun, source = "file") => {
    setGenerating(true);
    setAnalyzeError("");
    setAnalyzeResult(null);
    setFilters({});
    setSelectedRowId(null);
    try {
      // gurt-core валідує тіло запиту строгою (.strict()) zod-схемою: на
      // заявника приймає лише {id, privilege, values.{ses,dorm,act,zone}} --
      // будь-яке зайве поле (тут: pib/room/issues, потрібні лише для
      // локального звіту нижче) відхиляє весь запит як 400. buildAnalyzePayload
      // залишає тільки дозволені поля.
      const result = await analyzeAllocation(
        buildAnalyzePayload({
          places: placesForRun,
          runType,
          normalization: mode,
          // Проміжні величини TOPSIS для панелі "Розрахунок моделі".
          details: true,
          applicants,
        })
      );
      setAnalyzeResult({ result, applicants, places: placesForRun, source });
      setSortKey("modelRank");
      setSortDir("asc");
    } catch (err) {
      console.error(err);
      if (err instanceof DssApiError) {
        setAnalyzeError(`Помилка розрахунку (${err.status}): ${err.message}`);
      } else {
        setAnalyzeError("Не вдалося виконати розрахунок. Спробуйте ще раз.");
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleCalculate = async (modeOverride) => {
    const mode = typeof modeOverride === "string" ? modeOverride : normalization;
    if (acceptedRows.length === 0) {
      setAnalyzeError("Немає жодного прийнятого рядка для розрахунку.");
      return;
    }
    if (zoneMissingCount > 0) {
      setAnalyzeError("Спершу вкажіть віддаленість: у частини рядків її не розпізнано.");
      return;
    }
    const applicants = acceptedRows.map((r) => ({
      id: r.id,
      pib: r.pib,
      room: r.room,
      privilege: getPrivilege(r),
      // Пільга, виправлена вручну, більше не "за замовчуванням".
      issues:
        privilegeById[r.id] === undefined
          ? r.issues
          : r.issues.filter((i) => i.type !== "privilege_default"),
      values: r.values,
    }));
    await runAnalyze(applicants, mode, effectivePlaces, "file");
  };

  // Після «Зафіксувати» розрахунок запускається автоматично -- за тим, що
  // зафіксовано (віддаленість, пільга й прибрані рядки з пакета), а не за
  // початковим файлом. Кількість місць: введена вручну або всі зафіксовані.
  const handleCommitted = (batch) => {
    if (!batch || batch.status !== "committed") return;
    const committedRows = applyCommittedBatch(parsedRows, batch.rows);
    if (committedRows.length === 0) return;
    const applicants = committedRows.map((r) => ({
      id: r.id,
      pib: r.pib,
      room: r.room,
      privilege: r.values.privilege,
      issues: r.issues,
      values: r.values,
    }));
    runAnalyze(applicants, normalization, placesTouched ? effectivePlaces : applicants.length, "committed");
  };

  // Кількість місць у звіті оновлюється одразу при зміні поля. Лише після
  // автоматичного розрахунку за зафіксованим пакетом (поле не чіпали) типово
  // беруться всі зафіксовані рядки, а не всі рядки файлу.
  const reportPlaces =
    analyzeResult?.source === "committed" && !placesTouched ? analyzeResult.places : effectivePlaces;

  const reportRows = useMemo(() => {
    if (!analyzeResult) return [];
    return mergeReport({
      applicants: analyzeResult.applicants,
      analyzeResult: analyzeResult.result,
      places: reportPlaces,
    });
  }, [analyzeResult, reportPlaces]);

  // Знеособлення застосовується ДО сортування, фільтрації, таблиць і
  // вивантаження: далі за цим місцем реального ПІБ у рядках уже немає.
  const viewRows = useMemo(() => anonymizeRows(reportRows, anonymize), [reportRows, anonymize]);

  const discrepancies = useMemo(() => findDiscrepancies(viewRows), [viewRows]);
  const summary = useMemo(() => buildSummary(viewRows, reportPlaces), [viewRows, reportPlaces]);
  const cutoff = useMemo(
    () => computeCutoff(viewRows, analyzeResult?.result),
    [viewRows, analyzeResult]
  );
  const usedNormalization = analyzeResult?.result?.normalization || null;

  const handleSort = (key) => {
    if (key === sortKey) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
  };

  const sortedRows = useMemo(() => sortReportRows(viewRows, sortKey, sortDir), [viewRows, sortKey, sortDir]);
  const displayedRows = useMemo(
    () => (onlyLosing ? sortedRows.filter((r) => r.modelStatus === "loses") : sortedRows),
    [sortedRows, onlyLosing]
  );

  const filteredRows = useMemo(
    () => applyReportFilters(displayedRows, filters),
    [displayedRows, filters]
  );

  const { main: mainRows, noSession: noSessionRows } = useMemo(
    () => splitNoSession(filteredRows),
    [filteredRows]
  );
  const selectedRow = useMemo(
    () => viewRows.find((r) => r.id === selectedRowId) || null,
    [viewRows, selectedRowId]
  );

  const handleNormalizationChange = (mode) => {
    setNormalization(mode);
    // Режим змінює самі числа, тож звіт перераховується, а не лише
    // перемальовується -- на тих самих даних (файл або зафіксований пакет).
    if (analyzeResult?.source === "committed") {
      runAnalyze(analyzeResult.applicants, mode, analyzeResult.places, "committed");
    } else {
      handleCalculate(mode);
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleResetFilters = () => setFilters({});

  const handleExport = () => {
    if (viewRows.length === 0) return;
    exportMultiSheetToExcel(
      [
        { data: viewRows, sheetName: "Звіт", mapFn: (r) => reportExportRow(r, { anonymize }) },
        { data: discrepancies, sheetName: "Розбіжності", mapFn: (r) => discrepancyExportRow(r, { anonymize }) },
      ],
      `import-report-${Date.now()}.xlsx`
    );
  };

  const previewRows = parsedRows.slice(0, PREVIEW_LIMIT);
  const clampEvents = useMemo(
    () => collectClampEvents(analyzeResult?.result?.clamped, analyzeResult?.applicants),
    [analyzeResult]
  );
  const importCounters = useMemo(
    () => countImportIssues(analyzeResult?.applicants || []),
    [analyzeResult]
  );

  return (
    <div className="page-container">
      <h3 className="text-header">Імпорт рейтингу і звіт щодо перепоселення</h3>

      <div className="input-component">
        <p className="text-header">Файл рейтингу (.xlsx):</p>
        <input
          type="file"
          accept=".xlsx"
          aria-label="Файл рейтингу (.xlsx)"
          className="input-component"
          onChange={handleFileChange}
        />
        {fileName && <p className="text-header">Обрано файл: {fileName}</p>}
        <label className="text-header">
          <input
            type="checkbox"
            checked={anonymize}
            onChange={(e) => setAnonymize(e.target.checked)}
          />{" "}
          Знеособити (ПІБ, без кімнат)
        </label>
        {parseError && (
          <p className="text-header" style={{ color: "var(--full-busy)" }}>
            {parseError}
          </p>
        )}
      </div>

      {workbook && workbook.SheetNames.length > 1 && (
        <div className="header-content">
          <div className="input-block text-header">
            <p className="text-header">Аркуш:</p>
            <select
              className="header-content-select"
              value={sheetName}
              onChange={(e) => handleSheetChange(e.target.value)}
            >
              {workbook.SheetNames.map((name) => (
                <option key={name} value={name}>
                  {name}
                </option>
              ))}
            </select>
          </div>
        </div>
      )}

      {headerInfo && (
        <ColumnMapper
          foundHeaders={headerInfo.foundHeaders}
          headerEntries={headerInfo.headerEntries}
          indices={effectiveIndices}
          missing={missingColumns}
          onChange={handleManualColumnChange}
        />
      )}

      {headerInfo && missingColumns.length === 0 && (
        <>
          <DefaultZoneBlock
            missingCount={zoneMissingCount}
            defaultCount={zoneDefaultCount}
            defaultZone={defaultZone}
            onApply={setDefaultZone}
            onCancel={() => setDefaultZone(null)}
          />

          <div className="input-component">
            <h3 className="text-header">Попередній перегляд (перші {PREVIEW_LIMIT} рядків)</h3>
            <table className="table">
              <thead>
                <tr>
                  <th className="table-header">№</th>
                  <th className="table-header">ПІБ</th>
                  {!anonymize && <th className="table-header">Кімната</th>}
                  <th className="table-header" title="Сесія: бали за останню сесію (30–50; нижче 30 або порожньо — без сесії)">Сесія</th>
                  <th className="table-header" title="Гуртожиток: бали за активність у гуртожитку, подання завідувача (0–25)">Гуртожиток</th>
                  <th className="table-header" title="Діяльність: бали за громадську, спортивну та наукову діяльність (0–25)">Діяльність</th>
                  <th className="table-header" title="Віддаленість від дому: 1 — Вінницька ТГ, 2 — область, 3 — поза областю">Віддаленість</th>
                  <th className="table-header" title="Пільга: 1 — немає, 2 — ВПО та соціально вразливі, 3 — діти УБД, загиблих захисників, сироти. Жорстке обмеження черги, а не критерій">Пільга</th>
                  {hasActsColumn && (
                    <th className="table-header" title="Кількість актів порушень за даними файлу">Акти</th>
                  )}
                  <th className="table-header">Проблеми</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => (
                  <tr
                    key={row.id}
                    style={row.excluded ? { backgroundColor: "var(--half-busy)" } : undefined}
                  >
                    <td className="table-content">{row.rowNumber}</td>
                    <td className="table-content">{anonymize ? `Здобувач ${row.id}` : row.pib || "—"}</td>
                    {!anonymize && <td className="table-content">{row.room || "—"}</td>}
                    <ScoreCell row={row} field="ses" />
                    <ScoreCell row={row} field="dorm" />
                    <ScoreCell row={row} field="act" />
                    <ScoreCell row={row} field="zone" />
                    <td className="table-content">
                      {!row.excluded && (
                        <PrivilegeSelect
                          value={getPrivilege(row)}
                          onChange={(value) =>
                            setPrivilegeById((prev) => ({ ...prev, [row.id]: value }))
                          }
                          ariaLabel={`Пільга для рядка ${row.rowNumber}`}
                        />
                      )}
                    </td>
                    {hasActsColumn && <td className="table-content">{row.acts ?? "—"}</td>}
                    <td className="table-content">
                      <RowIssuesCell row={row} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p className="text-header">
              Прийнято рядків: {acceptedRows.length}. Виключено: {excludedCount}.
            </p>
          </div>

          <div className="input-component">
            <div className="form-container">
              <div className="input-block text-header">
                <p className="text-header">Кількість місць:</p>
                <input
                  type="number"
                  className="input-component-with-border"
                  value={placesTouched ? places : acceptedRows.length}
                  onChange={(e) => {
                    setPlacesTouched(true);
                    setPlaces(e.target.value);
                  }}
                />
                <p className="text-header places-hint">
                  Впливає лише на звіт (хто зберігає місце). У систему завантажуються всі прийняті рядки — кімнати
                  розподіляє «План розподілу».
                </p>
              </div>
              <div className="input-block text-header">
                <p className="text-header">Тип прогону:</p>
                <select
                  className="header-content-select"
                  value={runType}
                  onChange={(e) => setRunType(e.target.value)}
                >
                  {RUN_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-block text-header">
                <label className="text-header">
                  <input
                    type="checkbox"
                    checked={onlyLosing}
                    onChange={(e) => setOnlyLosing(e.target.checked)}
                  />{" "}
                  Показати лише тих, хто втрачає місце
                </label>
              </div>
            </div>

            <button
              className="submit-button"
              disabled={generating || acceptedRows.length === 0 || zoneMissingCount > 0}
              title={zoneMissingCount > 0 ? "Спершу вкажіть віддаленість для всіх рядків" : undefined}
              onClick={handleCalculate}
            >
              {generating ? "Розрахунок..." : "Розрахувати"}
            </button>
            <ImportToSystemPanel
              rows={uploadRows}
              fileHash={fileHash}
              sheetName={sheetName}
              anonymize={anonymize}
              defaultRunType={runType}
              onCommitted={handleCommitted}
              defaultZone={defaultZone}
              onDefaultZone={setDefaultZone}
              disabledReason={
                fileHash ? "" : "Не вдалося порахувати хеш файлу (потрібне безпечне з'єднання HTTPS)."
              }
            />
            {zoneMissingCount > 0 && (
              <p className="text-header">
                Розрахунок недоступний, поки віддаленість не вказана для всіх рядків.
              </p>
            )}
            {generating && (
              <p className="text-header">
                Перший запит після простою сервера може тривати до 60 секунд, будь
                ласка, зачекайте.
              </p>
            )}
            {analyzeError && (
              <p className="text-header" style={{ color: "var(--full-busy)" }}>
                {analyzeError}
              </p>
            )}
          </div>
        </>
      )}

      {analyzeResult && (
        <>
          <div className="input-component">
            <h3 className="text-header">Звіт</h3>
            {analyzeResult.source === "committed" && (
              <p className="text-header" data-testid="committed-calc-note">
                Розрахунок виконано автоматично після фіксації імпорту — за зафіксованими даними (без прибраних
                рядків, з віддаленістю й пільгою з попереднього перегляду).
              </p>
            )}
            <div className="form-container">
              <p className="input-block text-header">
                <strong>Усього претендентів:</strong> {summary.total}
              </p>
              <p className="input-block text-header">
                <strong>Місць:</strong> {summary.places}
              </p>
              <p className="input-block text-header">
                <strong>Зберігають місце:</strong> {summary.retains}
              </p>
              <p className="input-block text-header">
                <strong>Втрачають місце:</strong> {summary.loses}
              </p>
              <p className="input-block text-header">
                <strong>Прохідний показник TOPSIS*:</strong>{" "}
                {formatScore(cutoff)}
              </p>
            </div>
            <div className="form-container" data-testid="import-counters">
              <p className="input-block text-header">
                <strong>Обрізано значень:</strong> {importCounters.clamped}
              </p>
              <p className="input-block text-header">
                <strong>Без сесії (рядків):</strong> {importCounters.noSession}
              </p>
              <p className="input-block text-header">
                <strong>Віддаленість за замовчуванням (рядків):</strong> {importCounters.zoneDefault}
              </p>
              <p className="input-block text-header">
                <strong>Пільга за замовчуванням (рядків):</strong> {importCounters.privilegeDefault}
              </p>
            </div>
            {importCounters.zoneDefault > 0 && (
              <p className="text-header" style={{ color: "var(--full-busy)" }}>
                Увага: для {importCounters.zoneDefault} рядків віддаленість q задано за замовчуванням, а не взято з
                даних.
              </p>
            )}
          </div>

          <ImportReportFilters
            filters={filters}
            onFilterChange={handleFilterChange}
            onReset={handleResetFilters}
            normalization={normalization}
            onNormalizationChange={handleNormalizationChange}
            anonymize={anonymize}
            disabled={generating}
          />

          <ImportReportTable
            rows={mainRows}
            sortKey={sortKey}
            sortDir={sortDir}
            onSort={handleSort}
            anonymize={anonymize}
            onRowClick={(row) => setSelectedRowId(row.id)}
            selectedId={selectedRowId}
          />

          {selectedRow && (
            <CounterfactualPanel
              row={selectedRow}
              cutoff={cutoff}
              normalization={usedNormalization}
              onClose={() => setSelectedRowId(null)}
            />
          )}

          {noSessionRows.length > 0 && (
            <div className="input-component" data-testid="no-session-block">
              <h3 className="text-header">Без балів за сесію ({noSessionRows.length})</h3>
              <p className="text-header">
                У цих студентів немає балів за сесію (порожньо, 0 або нижче 30), тому їхній показник C не порівнюється з
                рештою: вони ранжуються окремою групою зі своєю матрицею й власними рангами та стоять у черзі після
                загального конкурсу.
              </p>
              <ImportReportTable
                rows={noSessionRows}
                sortKey={sortKey}
                sortDir={sortDir}
                onSort={handleSort}
                anonymize={anonymize}
                onRowClick={(row) => setSelectedRowId(row.id)}
                selectedId={selectedRowId}
              />
            </div>
          )}

          <ModelCalculationPanel
            model={analyzeResult.result?.model}
            rows={viewRows}
            clamped={clampEvents}
            normalization={usedNormalization}
          />

          <ImportDiscrepanciesBlock rows={discrepancies} anonymize={anonymize} />

          <button
            className="restore-button"
            style={{ width: "50%", marginLeft: 0, marginTop: "12px" }}
            onClick={handleExport}
          >
            Вивантажити звіт
          </button>
        </>
      )}
    </div>
  );
};

export default AllocationImportPage;
